import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import AdminIcon from './AdminIcon';

type Props = {
  survey: { id: string; mainTitle: string; stopCollect: boolean };
  busy: boolean;
  folderId?: string;
  onCopy: () => void;
  onCollect: () => void;
  onDelete: () => void;
};

export default function SurveyActions({ survey, busy, folderId, onCopy, onCollect, onDelete }: Props) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const id = encodeURIComponent(survey.id);
  const menuId = `survey-actions-${id}`;

  function close(restoreFocus = false) {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }

  function positionMenu() {
    if (!triggerRef.current || !menuRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > window.innerHeight || rect.right < 0 || rect.left > window.innerWidth) { close(); return; }
    const menu = menuRef.current;
    const height = menu.offsetHeight;
    // Position the portal before focusing it: focusing at its initial location can
    // scroll a long page back to the top and immediately dismiss the menu.
    menu.style.top = `${rect.bottom + height + 8 > window.innerHeight ? Math.max(8, rect.top - height - 6) : rect.bottom + 6}px`;
    menu.style.left = `${Math.max(8, Math.min(rect.right - 200, window.innerWidth - 208))}px`;
  }

  useLayoutEffect(() => {
    if (!open) return;
    positionMenu();
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus({ preventScroll: true });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const outside = (event: Event) => {
      if (!menuRef.current?.contains(event.target as Node) && !triggerRef.current?.contains(event.target as Node)) close();
    };
    const reposition = (event: Event) => {
      if (!menuRef.current?.contains(event.target as Node)) positionMenu();
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('touchstart', outside);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('touchstart', outside);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') { event.preventDefault(); close(true); return; }
    if (event.key === 'Tab') { close(true); return; }
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? []);
    const index = items.indexOf(document.activeElement as HTMLElement);
    let next = index;
    if (event.key === 'ArrowDown') next = (index + 1) % items.length;
    else if (event.key === 'ArrowUp') next = (index - 1 + items.length) % items.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = items.length - 1;
    else return;
    event.preventDefault();
    items[next]?.focus();
  }

  function run(action: () => void) { close(true); action(); }

  return <div className="dashboard-row-actions">
    <Link className="dashboard-row-action" to={`/editor/${id}${folderId ? `?folder=${encodeURIComponent(folderId)}` : ''}`} aria-label={`编辑：${survey.mainTitle}`}><AdminIcon name="edit" /><span>编辑</span></Link>
    <Link className="dashboard-row-action" to={`/records?survey=${id}${folderId ? `&folder=${encodeURIComponent(folderId)}` : ''}`} aria-label={`查看数据：${survey.mainTitle}`}><AdminIcon name="chart" /><span>数据</span></Link>
    <button ref={triggerRef} type="button" className={`dashboard-row-action dashboard-more${open ? ' is-open' : ''}`} disabled={busy} aria-label={`更多操作：${survey.mainTitle}`} aria-haspopup="menu" aria-expanded={open} aria-controls={open ? menuId : undefined} onClick={() => setOpen(!open)} onKeyDown={(event) => { if (event.key === 'ArrowDown') { event.preventDefault(); setOpen(true); } }}><AdminIcon name="more" /></button>
    {open && createPortal(<div ref={menuRef} id={menuId} className="admin-action-menu" role="menu" aria-label={`${survey.mainTitle}的操作`} onKeyDown={onKeyDown}>
      <Link to={`/s/${id}`} target="_blank" rel="noopener noreferrer" role="menuitem" tabIndex={-1} onClick={() => close(true)}><AdminIcon name="external" />打开填写页</Link>
      <button type="button" role="menuitem" tabIndex={-1} disabled={busy} onClick={() => run(onCopy)}><AdminIcon name="copy" />复制问卷</button>
      <button type="button" role="menuitem" tabIndex={-1} disabled={busy} onClick={() => run(onCollect)}><AdminIcon name={survey.stopCollect ? 'play' : 'pause'} />{survey.stopCollect ? '恢复收集' : '停止收集'}</button>
      <div className="admin-menu-divider" role="separator" />
      <button type="button" className="admin-menu-danger" role="menuitem" tabIndex={-1} disabled={busy} onClick={() => run(onDelete)}><AdminIcon name="trash" />删除问卷</button>
    </div>, document.body)}
  </div>;
}
